import {
  Audit,
  AuditList,
  FollowUp,
  FollowUpList,
  type AuditCancel,
  type AuditClose,
  type AuditConduct,
  type AuditPlan,
  type AuditQuery,
  type FollowUpComplete,
  type FollowUpQuery,
  CaseList,
  CaseOwnerList,
  CaseSubjectInstallationList,
  CaseSubjectUnitList,
  Inspection,
  InspectionList,
  type InspectionCancel,
  type InspectionComplete,
  type InspectionQuery,
  type InspectionSchedule,
  ComplianceCase,
  type CaseReassign,
  type CaseAcknowledge,
  type CaseClose,
  type CaseCorrection,
  type CaseEvidence,
  type CaseNote,
  type CaseOpen,
  type CaseQuery,
  type CaseStatusChange,
  ComplianceReport, type ReportQuery, MonitoringJob, MonitoringJobList, MonitoringRule, MonitoringRuleList, MonitoringRun, MonitoringRunList, RiskView, type MonitoringRuleInput, type MonitoringRunInput,
  Obligation, ObligationList, type ObligationInput,
  DecisionMakerList, EnforcementAction, EnforcementActionList, type EnforcementActionDraft, type EnforcementActionIssue, type EnforcementActionQuery, type EnforcementActionClose, type EnforcementActionWithdraw } from '@regigov/contracts';
import { ProblemDetails } from '@regigov/contracts';
import { api, ApiError, getToken } from './client';

const json = (body: unknown): RequestInit => ({ method: 'POST', body: JSON.stringify(body) });

export const monitoringApi = {
  rules: () => api('/compliance/monitoring/rules', MonitoringRuleList),
  publish: (input: MonitoringRuleInput) => api('/compliance/monitoring/rules', MonitoringRule, json(input)),
  runs: (ruleCode?: string) => api(`/compliance/monitoring/runs${ruleCode ? `?ruleCode=${encodeURIComponent(ruleCode)}` : ''}`, MonitoringRunList),
  run: (id: number) => api(`/compliance/monitoring/runs/${id}`, MonitoringRun),
  /** Starts a run in the background; poll `job` for progress. */
  start: (input: MonitoringRunInput) => api('/compliance/monitoring/jobs', MonitoringJob, json(input)),
  jobs: () => api('/compliance/monitoring/jobs', MonitoringJobList),
  job: (id: number) => api(`/compliance/monitoring/jobs/${id}`, MonitoringJob),
};

export const obligationsApi = {
  list: () => api('/compliance/obligations', ObligationList),
  publish: (input: ObligationInput) => api('/compliance/obligations', Obligation, json(input)),
};

export const riskApi = {
  view: () => api('/compliance/risk', RiskView),
};

const period = (q: ReportQuery) => new URLSearchParams(q).toString();

export const reportApi = {
  view: (q: ReportQuery) => api(`/compliance/report?${period(q)}`, ComplianceReport),
  /** Fetches the CSV with the session token and saves it under the server's filename. */
  downloadCsv: async (q: ReportQuery) => {
    const token = getToken();
    const res = await fetch(`/api/compliance/report.csv?${period(q)}`, { headers: token ? { authorization: `Bearer ${token}` } : {} });
    if (!res.ok) {
      const parsed = ProblemDetails.safeParse(await res.json().catch(() => null));
      throw new ApiError(res.status, parsed.success ? parsed.data : { type: 'about:blank', title: res.statusText, status: res.status });
    }
    const name = /filename="([^"]+)"/.exec(res.headers.get('content-disposition') ?? '')?.[1] ?? `compliance-activity-${q.from}-to-${q.to}.csv`;
    const href = URL.createObjectURL(await res.blob());
    const a = Object.assign(document.createElement('a'), { href, download: name });
    a.click();
    URL.revokeObjectURL(href);
  },
};

export const casesApi = {
  list: (query: CaseQuery = {}) => {
    const p = new URLSearchParams(Object.entries(query).filter((e): e is [string, string] => Boolean(e[1])));
    return api(`/compliance/cases${p.size ? `?${p}` : ''}`, CaseList);
  },
  owners: () => api('/compliance/cases/owners', CaseOwnerList),
  subjectUnits: (participantId: string) => api(`/compliance/cases/subject-units?participantId=${encodeURIComponent(participantId)}`, CaseSubjectUnitList),
  subjectInstallations: (participantId: string) => api(`/compliance/cases/subject-installations?participantId=${encodeURIComponent(participantId)}`, CaseSubjectInstallationList),
  get: (id: string) => api(`/compliance/cases/${encodeURIComponent(id)}`, ComplianceCase),
  open: (input: CaseOpen) => api('/compliance/cases', ComplianceCase, json(input)),
  status: (id: string, input: CaseStatusChange) => api(`/compliance/cases/${encodeURIComponent(id)}/status`, ComplianceCase, json(input)),
  close: (id: string, input: CaseClose) => api(`/compliance/cases/${encodeURIComponent(id)}/close`, ComplianceCase, json(input)),
  reassign: (id: string, input: CaseReassign) => api(`/compliance/cases/${encodeURIComponent(id)}/owner`, ComplianceCase, json(input)),
  note: (id: string, input: CaseNote) => api(`/compliance/cases/${encodeURIComponent(id)}/notes`, ComplianceCase, json(input)),
  acknowledge: (id: string, input: CaseAcknowledge) => api(`/compliance/cases/${encodeURIComponent(id)}/acknowledge`, ComplianceCase, json(input)),
  evidence: (id: string, input: CaseEvidence) => api(`/compliance/cases/${encodeURIComponent(id)}/evidence`, ComplianceCase, json(input)),
  correct: (id: string, input: CaseCorrection) => api(`/compliance/cases/${encodeURIComponent(id)}/corrections`, ComplianceCase, json(input)),
};

export const inspectionsApi = {
  list: (query: InspectionQuery = {}) => {
    const p = new URLSearchParams(Object.entries(query).filter((e): e is [string, string] => Boolean(e[1])));
    return api(`/compliance/inspections${p.size ? `?${p}` : ''}`, InspectionList);
  },
  schedule: (input: InspectionSchedule) => api('/compliance/inspections', Inspection, json(input)),
  complete: (id: string, input: InspectionComplete) => api(`/compliance/inspections/${encodeURIComponent(id)}/complete`, Inspection, json(input)),
  cancel: (id: string, input: InspectionCancel) => api(`/compliance/inspections/${encodeURIComponent(id)}/cancel`, Inspection, json(input)),
};

const query = (q: object) => {
  const p = new URLSearchParams(Object.entries(q).filter((e): e is [string, string] => Boolean(e[1])));
  return p.size ? `?${p}` : '';
};

export const auditsApi = {
  list: (q: AuditQuery = {}) => api(`/compliance/audits${query(q)}`, AuditList),
  plan: (input: AuditPlan) => api('/compliance/audits', Audit, json(input)),
  conduct: (id: string, input: AuditConduct) => api(`/compliance/audits/${encodeURIComponent(id)}/conduct`, Audit, json(input)),
  close: (id: string, input: AuditClose) => api(`/compliance/audits/${encodeURIComponent(id)}/close`, Audit, json(input)),
  cancel: (id: string, input: AuditCancel) => api(`/compliance/audits/${encodeURIComponent(id)}/cancel`, Audit, json(input)),
};

export const followUpsApi = {
  list: (q: FollowUpQuery = {}) => api(`/compliance/follow-ups${query(q)}`, FollowUpList),
  complete: (id: number, input: FollowUpComplete) => api(`/compliance/follow-ups/${id}/complete`, FollowUp, json(input)),
};

export const enforcementApi = {
  list: (q: EnforcementActionQuery = {}) => api(`/compliance/enforcement-actions${query(q)}`, EnforcementActionList),
  decisionMakers: () => api('/compliance/enforcement-actions/decision-makers', DecisionMakerList),
  draft: (input: EnforcementActionDraft) => api('/compliance/enforcement-actions', EnforcementAction, json(input)),
  issue: (id: string, input: EnforcementActionIssue) => api(`/compliance/enforcement-actions/${encodeURIComponent(id)}/issue`, EnforcementAction, json(input)),
  withdraw: (id: string, input: EnforcementActionWithdraw) => api(`/compliance/enforcement-actions/${encodeURIComponent(id)}/withdraw`, EnforcementAction, json(input)),
  close: (id: string, input: EnforcementActionClose) => api(`/compliance/enforcement-actions/${encodeURIComponent(id)}/close`, EnforcementAction, json(input)),
};
